import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { jobsEndpoint } from "../constants";
import JobFilter from "../components/JobFilter";

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("token")
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        const queryParams = new URLSearchParams(filters).toString();
        const response = await axios.get(`${jobsEndpoint}my_jobs/?${queryParams}`,{
          headers: {
           'Authorization': `Token ${token}`, 
          },
        });
        setJobs(response.data.results || response.data);
      } catch (err) {
        setError("Failed to load jobs.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [filters]);

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const toggleDropdown = (jobId) => {
    setDropdownOpen(dropdownOpen === jobId ? null : jobId);
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await axios.delete(`${jobsEndpoint}${jobId}/delete/`, {
          headers: {
            'Authorization': `Token ${token}`,
          }
        });
      setJobs(jobs.filter(job => job.id !== jobId));
      setDeleteConfirm(null);
      setDropdownOpen(null);
    } catch (err) {
      console.error("Failed to delete job:", err);
      setError("Failed to delete job. Please try again.");
    }
  };

  const confirmDelete = (e, jobId) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteConfirm(jobId);
    setDropdownOpen(null);
  };

  const sortedJobs = useMemo(() => {
    let result = [...jobs];

    if (sortConfig.key) {
      result.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [jobs, sortConfig]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Don't close if clicking on delete buttons or modal
      if (e.target.closest('.dropdown-menu') || e.target.closest('.delete-modal')) {
        return;
      }
      setDropdownOpen(null);
      setDeleteConfirm(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="lg:p-8">
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4 delete-modal">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Job</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this job? This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteConfirm(null);
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteJob(deleteConfirm);
                }}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Jobs</h1>
          <p className="mt-1 text-sm text-gray-600">Overview of all job posts.</p>
        </div>
        <div className="flex items-center gap-4">

          <button
            onClick={() => navigate("/create_jobs")}
            className="px-4 py-2 text-white bg-[#19bfb7] rounded-lg hover:bg-[#18ada6]"
          >
            Create Job
          </button>
        </div>
      </div>

      <JobFilter onApplyFilters={handleApplyFilters} />

      {/* Loading/Error */}
      {loading && <p className="text-gray-500">Loading jobs...</p>}
      {error && <p className="text-red-500">{error}</p>}

      {/* Jobs Table */}
      {!loading && !error && (
        <div className="bg-white rounded-xl border border-gray-200 mt-10">
          <div className="pt-3 px-5">
            <h3 className="text-sm font-semibold text-gray-900">Total Jobs</h3>
          </div>
          <div className="flex gap-3 items-end py-4 px-5 border-b border-gray-200">
            <h3 className="text-2xl text-gray-900 font-semibold">
              {sortedJobs.length}
            </h3>
            <div className="flex items-center gap-1 text-sm text-emerald-600">
              <span>↑ 8.6%</span>
              <span className="text-gray-600 ml-1">vs last 30 days</span>
            </div>
          </div>

          <div>
            <div className="px-5 py-3 grid md:grid-cols-8 grid-cols-5 border-b border-gray-200">
              <div className="flex gap-3 items-center md:col-span-3 col-span-2">
                <input type="checkbox" className="rounded border-gray-300" />
                <h5 className="text-gray-500 font-semibold">Job Title</h5>
              </div>
              <div className="grid md:grid-cols-5 grid-cols-3 md:col-span-5 col-span-3">
                <h5 className="text-gray-500 font-semibold text-xs">Department</h5>
                <h5 className="text-gray-500 font-semibold text-xs hidden md:block">Applicants</h5>
                <h5 className="text-gray-500 font-semibold text-xs">Avg. AI Score</h5>
                <h5 className="text-gray-500 font-semibold text-xs">Status</h5>
                <h5 className="text-gray-500 font-semibold text-xs hidden md:block">Posted Date</h5>
              </div>
            </div>

            {sortedJobs.map((job) => (
              <div key={job.id} className="grid md:grid-cols-8 grid-cols-5 items-center p-4 border-b border-gray-200">
                <div className="flex items-center gap-3 md:col-span-3 col-span-2">
                  <input type="checkbox" className="rounded border-gray-300" />
                  <p className="md:font-medium font-bold text-xs text-gray-900">{job.title}</p>
                </div>
                <div>
                  <p className="font-medium text-xs text-gray-500">{job.company}</p>
                </div>
                <div className="hidden md:block">
                  <p className="font-medium text-xs text-gray-500">--</p>
                </div>
                <div>
                  <p className="font-medium text-xs text-gray-500">95</p>
                </div>
                <div>
                  <span className="text-center text-[12px] border py-1 px-2 rounded-md bg-green-100 text-green-600 border-green-400">
                    Active
                  </span>
                  <div className="relative md:hidden">
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDropdown(job.id);
                      }}
                      className="text-gray-600 cursor-pointer text-xl font-bold"
                    >
                      &#8942;
                    </span>
                    {dropdownOpen === job.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10 dropdown-menu">
                        <div className="py-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/applicants/${job.id}`);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            View Applicants
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/job_details/${job.id}`);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            View Job details
                          </button>
                          <button
                            onClick={(e) => confirmDelete(e, job.id)}
                            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                          >
                            Delete Job
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="hidden md:flex justify-between items-center">
                  <span className="font-medium text-xs text-gray-500">
                    {new Date(job.date_updated).toLocaleDateString()}
                  </span>
                  <div className="relative">
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDropdown(job.id);
                      }}
                      className="text-gray-600 cursor-pointer text-xl font-bold xl:ml-10"
                    >
                      &#8942;
                    </span>
                    {dropdownOpen === job.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10 dropdown-menu">
                        <div className="py-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/applicants/${job.id}`);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            View Applicants
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/edit_job/${job.id}`);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Edit Job
                          </button>
  
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/job_details/${job.id}`);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            View Job details
                          </button>
                          <button
                            onClick={(e) => confirmDelete(e, job.id)}
                            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                          >
                            Delete Job
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Jobs;